<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { useStore } from 'vuex';

const props = defineProps({
  integration: {
    type: String,
    default: 'inovafarma',
    validator: value => ['inovafarma', 'atec'].includes(value),
  },
});

const emit = defineEmits(['add-to-cart']);

// Vuex store-backed products
const store = useStore();

const storeKey = computed(() =>
  props.integration === 'atec' ? 'atecProducts' : 'integrahubProducts'
);

const page = ref(1);
const limit = ref(50);
const totalCount = ref(0);

const searchQuery = ref('');

const currentAccountId = computed(() => store.getters.getCurrentAccountId);
const showOutOfStock = computed(() =>
  store.getters['accounts/showOutOfStockProducts'](currentAccountId.value)
);
const normalizedSearchTerm = computed(() =>
  String(searchQuery.value || '')
    .trim()
    .toLowerCase()
);
const normalizedSearchDigits = computed(() =>
  String(searchQuery.value || '').replace(/\D/g, '')
);
const isEanSearch = computed(() => normalizedSearchDigits.value.length >= 8);

const matchesProductSearch = product => {
  if (!normalizedSearchTerm.value) return true;

  const productName = String(product.name || '').toLowerCase();
  const productSku = String(product.sku || '').toLowerCase();
  const productEan = String(product.ean || '').toLowerCase();
  const productEanDigits = productEan.replace(/\D/g, '');

  return (
    productName.includes(normalizedSearchTerm.value) ||
    productSku.includes(normalizedSearchTerm.value) ||
    productEan.includes(normalizedSearchTerm.value) ||
    (normalizedSearchDigits.value &&
      productEanDigits.includes(normalizedSearchDigits.value))
  );
};

const filteredProducts = computed(() => {
  let items = store.state[storeKey.value]?.items || [];

  // Filter out-of-stock products based on account setting
  if (!showOutOfStock.value) {
    items = items.filter(p => p.stock > 0);
  }

  return items.filter(matchesProductSearch);
});

const fetchProducts = async () => {
  const searchTerm = searchQuery.value?.trim() || '';
  const requestLimit = isEanSearch.value
    ? Math.max(limit.value, 200)
    : limit.value;
  const eanSearchValue = isEanSearch.value
    ? normalizedSearchDigits.value
    : undefined;

  if (isEanSearch.value && props.integration !== 'atec') {
    await store.dispatch('integrahubProducts/fetchByEan', {
      ean: eanSearchValue,
      limit: requestLimit,
    });
    totalCount.value = filteredProducts.value.length;
    return;
  }

  await store.dispatch(`${storeKey.value}/fetch`, {
    q: searchTerm || undefined,
    limit: requestLimit,
    page: page.value,
  });

  const pagination = store.state[storeKey.value]?.pagination || {};
  totalCount.value = pagination.count || 0;
  if (!pagination.count && filteredProducts.value.length < requestLimit) {
    totalCount.value =
      (page.value - 1) * requestLimit + filteredProducts.value.length;
  }
};

const loading = computed(
  () => store.state[storeKey.value]?.uiFlags?.isFetching
);

let debounceTimer = null;
watch(searchQuery, () => {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    page.value = 1;
    fetchProducts();
  }, 350);
});

const totalPages = computed(() => {
  if (isEanSearch.value) return 1;
  if (!totalCount.value) return 1;
  return Math.max(1, Math.ceil(totalCount.value / limit.value));
});

const canGoPrev = computed(() => page.value > 1);
const canGoNext = computed(() => page.value < totalPages.value);

const goToPrevPage = () => {
  if (!canGoPrev.value) return;
  page.value -= 1;
  fetchProducts();
};

const goToNextPage = () => {
  if (!canGoNext.value) return;
  page.value += 1;
  fetchProducts();
};

onMounted(() => {
  fetchProducts();
});

const formatPrice = price => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(price);
};

const handleAddToCart = product => {
  emit('add-to-cart', product);
};
</script>

<template>
  <div class="flex flex-col h-full">
    <!-- Busca -->
    <div class="p-4 border-b border-n-weak">
      <Input
        v-model="searchQuery"
        :placeholder="$t('PDV.SEARCH_PRODUCTS_WITH_EAN')"
        icon="i-ph-magnifying-glass-bold"
      />
    </div>

    <div
      class="px-4 py-2 border-b border-n-weak flex items-center justify-between gap-2"
    >
      <Button sm ghost :disabled="!canGoPrev || loading" @click="goToPrevPage">
        <span class="i-lucide-chevron-left mr-1" />
        {{ $t('PDV.PAGINATION_PREV') }}
      </Button>

      <span class="text-xs text-n-text-subtle">
        {{
          $t('PDV.PAGINATION_STATUS', {
            page,
            total: totalPages,
            count: totalCount,
          })
        }}
      </span>

      <Button sm ghost :disabled="!canGoNext || loading" @click="goToNextPage">
        {{ $t('PDV.PAGINATION_NEXT') }}
        <span class="i-lucide-chevron-right ml-1" />
      </Button>
    </div>

    <!-- Lista de Produtos -->
    <div class="flex-1 overflow-y-auto p-4 space-y-3">
      <div
        v-if="!loading && filteredProducts.length === 0"
        class="flex flex-col items-center justify-center h-full text-center py-8"
      >
        <span class="text-4xl mb-2">📦</span>
        <p class="text-n-text-subtle">
          {{ $t('PDV.NO_PRODUCTS_FOUND') }}
        </p>
      </div>

      <div v-if="loading" class="flex items-center justify-center py-8">
        <span>{{ $t('PDV.LOADING') }}</span>
      </div>

      <div
        v-for="product in filteredProducts"
        :key="product.id"
        class="bg-n-solid-1 rounded-lg p-4 border border-n-weak hover:border-n-brand transition-colors"
      >
        <div class="flex gap-3">
          <!-- Imagem do Produto -->
          <div
            class="w-16 h-16 bg-n-solid-2 rounded flex items-center justify-center flex-shrink-0"
          >
            <span class="text-2xl">📦</span>
          </div>

          <!-- Informações do Produto -->
          <div class="flex-1 min-w-0">
            <h4
              class="font-semibold text-n-text truncate"
              :title="product.name"
            >
              {{ product.name }}
            </h4>
            <p class="text-xs text-n-text-subtle">SKU: {{ product.sku }}</p>
            <p v-if="product.brand" class="text-xs text-n-text-subtle truncate">
              {{ product.brand }}
            </p>
            <div class="flex items-center justify-between mt-2">
              <span class="text-lg font-bold text-n-brand">
                {{ formatPrice(product.price) }}
              </span>
              <span
                class="text-xs px-2 py-1 rounded"
                :class="
                  product.stock > 5
                    ? 'bg-green-100 text-green-800'
                    : product.stock > 0
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-red-100 text-red-800'
                "
              >
                {{ product.stock }} em estoque
              </span>
            </div>
          </div>
        </div>

        <!-- Botão Adicionar -->
        <Button
          class="w-full mt-3"
          sm
          :color="product.stock === 0 ? 'ruby' : 'blue'"
          @click="handleAddToCart(product)"
        >
          <span class="i-ph-shopping-cart-bold mr-2" />
          {{ $t('PDV.ADD_TO_CART') }}
        </Button>
      </div>
    </div>
  </div>
</template>
